import { ChangeEvent, FC, ReactElement } from 'react';
import { Box, makeStyles, Tab, Tabs, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { useWbTabsContext } from './WbTabsContext';
import { WbTooltip } from '../WbTooltip';

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      width: '100%',
    },
    indicator: {
      display: 'none',
    },
    verticalTabsContainer: {
      flexGrow: 1,
      display: 'flex',
      height: '100%',
      width: '100%',
    },
    tabRoot: {
      width: '100%',
      maxWidth: '100%',
      textTransform: 'none',
      padding: theme.spacing(0.5, 1),
      height: 40,
      borderRadius: 0,
      borderRight: `1px solid transparent`,
      borderLeft: `1px solid transparent`,
      borderBottom: `1px solid ${theme.palette.grey[200]}`,
      color: theme.palette.text.primary,
      fontWeight: 500,
      opacity: 1,
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.text.primary,
      },
    },
    selectedTab: {
      backgroundColor: theme.palette.grey[100],
      border: `1px solid ${theme.palette.blueGrey[100]}`,
      color: theme.palette.text.primary,
      fontWeight: 500,
      borderRadius: 6,
      '&:hover': {
        backgroundColor: theme.palette.action.selected,
      },
    },
    nextTabSelected: {
      borderBottom: 'none',
    },
    tab: {
      color: theme.palette.text.primary,
    },
    labelIcon: {
      minHeight: 'auto',
    },
    wrapperWithIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexDirection: 'row-reverse',
      '&>:first-child': {
        marginBottom: '0px !important',
      },
    },
    wrapperWithoutIcon: {
      display: 'flex',
      alignItems: 'start',
      justifyContent: 'space-between',
    },
  }),
  { name: 'WbVerticalTabs' },
);

interface VerticalTabItem {
  label: ReactElement | string;
  value: any;
  disabled?: boolean;
  tooltip?: string;
  icon?: ReactElement;
}

interface WbVerticalTabsProps {
  tabs: VerticalTabItem[];
  onValueChange?: (value: number) => void;
  'aria-label'?: string;
}

const a11yProps = (index: number) => {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  };
};

export const WbVerticalTabs: FC<WbVerticalTabsProps> = ({
  tabs,
  onValueChange,
  'aria-label': ariaLabel = 'Vertical tabs',
}) => {
  const classes = useStyles();
  const { value, onChange } = useWbTabsContext();

  const handleChange = (event: ChangeEvent<{}>, newValue: any) => {
    onChange(event, newValue);
    onValueChange?.(newValue);
  };

  return (
    <Box className={clsx(classes.verticalTabsContainer)}>
      <Tabs
        orientation="vertical"
        variant="scrollable"
        value={value}
        onChange={handleChange}
        aria-label={ariaLabel}
        classes={{
          root: classes.root,
          indicator: classes.indicator,
        }}
      >
        {tabs.map((tab, index) =>
          tab.tooltip ? (
            <WbTooltip title={tab.tooltip} key={index}>
              <Tab
                label={tab.label}
                icon={tab.icon}
                value={tab.value}
                disabled={tab.disabled}
                classes={{
                  root: clsx(classes.tabRoot, {
                    [classes.nextTabSelected]: value === index + 1,
                  }),
                  selected: classes.selectedTab,
                  labelIcon: classes.labelIcon,
                  wrapper: tab?.icon
                    ? classes.wrapperWithIcon
                    : classes.wrapperWithoutIcon,
                }}
                {...a11yProps(index)}
              />
            </WbTooltip>
          ) : (
            <Tab
              key={index}
              label={tab.label}
              icon={tab.icon}
              value={index}
              disabled={tab.disabled}
              classes={{
                root: clsx(classes.tabRoot, {
                  [classes.nextTabSelected]: value === index + 1,
                }),
                selected: classes.selectedTab,
                labelIcon: classes.labelIcon,
                wrapper: tab?.icon
                  ? classes.wrapperWithIcon
                  : classes.wrapperWithoutIcon,
              }}
              {...a11yProps(index)}
            />
          ),
        )}
      </Tabs>
    </Box>
  );
};
